import { useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ButtonPrimary,
  IconExport,
  ButtonSize,
  Table,
  PermissionEnum,
  SchemaFilterType,
  AccountsSelector,
  SortDir,
  PermissionDenied,
  IconDownload,
  ButtonSecondary,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { Category } from '@ui/src/components/accounts-selector/types';
import { useAppSelector } from '@store';
import { StatementListItem } from './types';
import formatStatementsList from './helpers/format-statements-list';
import useGetListFunctions from './hooks/use-get-list-functions';
import ExportStatement from '../../../components/statements/export/export';
import { columnsItems, columnsLabels } from './variables/list';

import cn from './styles.module.css';

const StatementsList = (): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { statements, pagination, accountCategories, errorsList } =
    useAppSelector((state) => state.accountsStatements.list);
  const { currencies: currenciesList, currenciesList: currencies } =
    useAppSelector((state) => state.root);
  const { activePermissions } = useAppSelector((state) => state.root);

  const { search } = useLocation();

  const {
    handleSubmit,
    downloadedId,
    statementError,
    statementPanelIsVisible,
    statementFileError,
    accountsCategoriesLoading,
    accountsListLoading,
    accountsListUpdateInProgress,
    downloadInProgress,
    downloadIsFetching,
    fileIsLoading,
    fileIsFetching,
    isLoading,
    onSort,
    setPage,
    errorList,
    getAccountsListAction,
    onDownloadStatement,
    onSearch,
    onStatementsDownload,
    filtersComponent,
    filters,
    selectedAccount,
    accounts,
    setAccounts,
    sort,
    setStatementPanelIsVisible,
    setSelectedCurrency,
    onFilter,
    isFetching,
    onCloseStatementPanel,
    selectedCurrency,
    selectedDateType,
    selectedFormat,
    selectedDateRange,
    setSelectedDateType,
    setSelectedDateRange,
    setSelectedFormat,
    onSelectedAccountClick,
  } = useGetListFunctions({
    currencies,
    statements,
    activePermissions,
    search,
    scrollRef,
  });
  const formattedData = useMemo(() => {
    const data = formatStatementsList(
      statements as StatementListItem[],
      filters,
      onDownloadStatement,
      currencies,
      currenciesList,
      downloadInProgress || downloadIsFetching,
      downloadedId,
      selectedAccount,
      activePermissions?.includes(
        PermissionEnum.ACCOUNTS_STATEMENTS_EXPORT_ACTION,
      ),
    );

    return data;
  }, [
    statements,
    filters,
    currencies,
    currenciesList,
    downloadInProgress,
    downloadIsFetching,
    downloadedId,
    selectedAccount,
    activePermissions,
  ]);

  let content;

  if (activePermissions?.includes(PermissionEnum.ACCOUNTS_STATEMENTS)) {
    content = (
      <>
        <div className={cn.statementsControls}>
          <div className={cn.statementsTitle}>Statement of account</div>
          <div className={cn.accountSelectorWrapper}>
            <AccountsSelector
              accounts={accountCategories as Category[]}
              placeholder="Enter name or number..."
              isLoading={accountsCategoriesLoading}
              selectedAccount={selectedAccount}
              accountsIsLoading={
                accountsListLoading || accountsListUpdateInProgress
              }
              onSearch={onSearch}
              setAccountQuery={getAccountsListAction}
              accountsList={accounts}
              setAccounts={setAccounts}
              setSelectedAccount={(number) => onSelectedAccountClick(number)}
            />
          </div>
          {activePermissions?.includes(
            PermissionEnum.ACCOUNTS_STATEMENTS_EXPORT_ACTION,
          ) ? (
            <ButtonPrimary
              disabled={Boolean(!statements?.length)}
              icon={<IconExport />}
              size={ButtonSize.SMALL}
              text="Export"
              onClick={() => {
                setStatementPanelIsVisible(true);

                if (filters?.currencyId) {
                  setSelectedCurrency(filters?.currencyId?.value[0]);
                }
              }}
            />
          ) : (
            <div />
          )}
          {statementError && (
            <div className={cn.downloadStatementError}>{statementError}</div>
          )}
        </div>

        <div className={cn.statementsTableWrapper}>
          <Table
            columns={columnsItems}
            data={formattedData || []}
            onSort={(name) => onSort(name)}
            filters={filters}
            sort={sort}
            columnsLabels={columnsLabels}
            onFilterChange={(values) => {
              onFilter({
                ...values,
                account: {
                  value: selectedAccount ? [selectedAccount] : [],
                  type: SchemaFilterType.STRING,
                },
                sortProp: {
                  value: sort.sortProp ? [sort.sortProp] : ['transferDate'],
                  type: SchemaFilterType.STRING,
                },
                sortDir: {
                  value: sort.sortDir ? [sort.sortDir] : [SortDir.DESC],
                  type: SchemaFilterType.STRING,
                },
              });
            }}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching}
            storeKey="statements-of-accounts"
            pagination={pagination}
            tableBodyClassName={`${
              !selectedAccount ? cn.emptySeletedAccount : ''
            } ${isLoading || isFetching ? cn.tableLoading : ''}`}
            take="30"
            setPage={(data) => setPage({ ...data, filters })}
            error={errorsList || errorList}
            withSettings
            clickable
          />
          {!selectedAccount && (
            <div className={cn.selectAccountText}>
              Select account to view statement
            </div>
          )}
        </div>
      </>
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div
      className={`${cn.statementsBottom} ${
        !selectedAccount || formattedData?.length === 0 ? cn.overflowHidden : ''
      }`}
      ref={scrollRef}
    >
      {content}
      <SidePanel
        title={`Export statement of ${selectedAccount}`}
        isOpen={statementPanelIsVisible}
        onClose={onCloseStatementPanel}
        type={SidePanelType.AML}
        errorSidepanel={statementFileError}
        sidepanelExpandedKey="statements-export"
        footer={
          <div className={cn.statementPanelControls}>
            <ButtonPrimary
              text={isLoading || isFetching ? 'Loading...' : 'Download'}
              icon={<IconDownload />}
              disabled={
                !selectedCurrency ||
                !selectedDateType ||
                !selectedFormat ||
                fileIsLoading ||
                fileIsFetching ||
                (selectedDateType === 'custom' && !selectedDateRange.length)
              }
              onClick={onStatementsDownload}
              bold
            />
            <ButtonSecondary
              className="button-white"
              text="Close"
              onClick={onCloseStatementPanel}
              bold
            />
          </div>
        }
        closeOnClickOutside
      >
        <ExportStatement
          selectedCurrency={selectedCurrency}
          currencies={currencies}
          setSelectedCurrency={setSelectedCurrency}
          selectedDateType={selectedDateType}
          setSelectedDateType={setSelectedDateType}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
          selectedFormat={selectedFormat}
          setSelectedFormat={setSelectedFormat}
        />
      </SidePanel>
    </div>
  );
};

export default StatementsList;
